import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  GridLegacy as Grid,
  Card,
  CardContent,
  Chip,
  Avatar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Divider,
  Stack,
  Rating,
  LinearProgress,
  Badge,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  InputAdornment
} from '@mui/material';
import {
  Search,
  FilterAlt,
  Refresh,
  Assignment,
  LocalOffer,
  Person,
  Star,
  ThumbUp,
  ThumbDown,
  Schedule,
  TrendingUp,
  Assessment,
  ExpandMore,
  Download,
  Close,
  CheckCircle,
  Cancel,
  HelpOutline,
  SortByAlpha,
  CalendarToday,
  RateReview,
} from '@mui/icons-material';
import { StatCard, Section, SectionHeader, EmptyState, StatusChip } from '../shared/ui';
import { tokens } from '../../theme';
import { format, isValid } from 'date-fns';
import { adminAPI } from '../../services/api';
import ResumeActions from '../shared/ResumeViewer';

const CandidateReviews = () => {
  // ===== STATE MANAGEMENT =====
  const [reviews, setReviews] = useState([]);
  const [filteredReviews, setFilteredReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  
  // Filters
  const [filters, setFilters] = useState({
    status: '',
    panelist: '',
    rating_min: 0,
    rating_max: 5,
    search: '',
    date_from: '',
    date_to: ''
  });
  
  // UI State
  const [selectedReview, setSelectedReview] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [sortBy, setSortBy] = useState('submitted_at');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // Analytics
  const [analytics, setAnalytics] = useState({
    total_reviews: 0,
    approved: 0,
    rejected: 0,
    needs_review: 0,
    avg_rating: 0,
    panelist_stats: []
  });

  // ===== LOAD DATA =====
  const loadCVReviews = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      console.log('🔄 Loading CV reviews...');
      
      // We'll need to add this endpoint to adminAPI
      const response = await adminAPI.getCVReviews({
        page: 1,
        per_page: 1000, // Load all for local filtering
        sort_by: sortBy,
        sort_order: sortOrder
      });
      
      if (response.data.status === 'success') {
        const reviewsData = response.data.cv_reviews || [];
        setReviews(reviewsData);
        setFilteredReviews(reviewsData);
        
        // Calculate analytics
        calculateAnalytics(reviewsData);
        
        console.log(`✅ Loaded ${reviewsData.length} CV reviews`);
      } else {
        setError(response.data.error || 'Failed to load CV reviews');
      }
    } catch (error) {
      console.error('❌ Failed to load CV reviews:', error);
      setError('Failed to load CV reviews. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [sortBy, sortOrder]);

  // ===== ANALYTICS CALCULATION =====
  const calculateAnalytics = (reviewsData) => {
    const total_reviews = reviewsData.length;
    const approved = reviewsData.filter(r => r.status === 'approved').length;
    const rejected = reviewsData.filter(r => r.status === 'rejected').length;
    const needs_review = reviewsData.filter(r => r.status === 'needs_review').length;
    
    const ratings = reviewsData.filter(r => r.rating > 0).map(r => r.rating);
    const avg_rating = ratings.length > 0 ? ratings.reduce((a, b) => a + b, 0) / ratings.length : 0;
    
    // Panelist statistics
    const panelistStats = {};
    reviewsData.forEach(review => {
      const panelistId = review.panelist_id;
      if (!panelistStats[panelistId]) {
        panelistStats[panelistId] = {
          panelist_name: review.panelist_name,
          total: 0,
          approved: 0,
          rejected: 0,
          avg_rating: 0,
          ratings: []
        };
      }
      panelistStats[panelistId].total++;
      if (review.status === 'approved') panelistStats[panelistId].approved++;
      if (review.status === 'rejected') panelistStats[panelistId].rejected++;
      if (review.rating > 0) panelistStats[panelistId].ratings.push(review.rating);
    });
    
    // Calculate average ratings for each panelist
    Object.keys(panelistStats).forEach(panelistId => {
      const ratings = panelistStats[panelistId].ratings;
      panelistStats[panelistId].avg_rating = ratings.length > 0 
        ? ratings.reduce((a, b) => a + b, 0) / ratings.length 
        : 0;
    });
    
    setAnalytics({
      total_reviews,
      approved,
      rejected,
      needs_review,
      avg_rating: parseFloat(avg_rating.toFixed(2)),
      panelist_stats: Object.values(panelistStats)
    });
  };

  // ===== FILTERING =====
  const applyFilters = useCallback(() => {
    let filtered = [...reviews];
    
    // Status filter
    if (filters.status) {
      filtered = filtered.filter(review => review.status === filters.status);
    }
    
    // Panelist filter
    if (filters.panelist) {
      filtered = filtered.filter(review => 
        review.panelist_name?.toLowerCase().includes(filters.panelist.toLowerCase()) ||
        review.panelist_id.toString() === filters.panelist
      );
    }
    
    // Rating range filter
    filtered = filtered.filter(review => 
      review.rating >= filters.rating_min && review.rating <= filters.rating_max
    );
    
    // Search filter (candidate name, notes, etc.)
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(review =>
        review.candidate_name?.toLowerCase().includes(searchLower) ||
        review.notes?.toLowerCase().includes(searchLower) ||
        review.strengths?.toLowerCase().includes(searchLower) ||
        review.weaknesses?.toLowerCase().includes(searchLower) ||
        review.recommendations?.toLowerCase().includes(searchLower)
      );
    }
    
    // Date filters
    if (filters.date_from) {
      const fromDate = new Date(filters.date_from);
      filtered = filtered.filter(review => new Date(review.submitted_at) >= fromDate);
    }
    
    if (filters.date_to) {
      const toDate = new Date(filters.date_to);
      toDate.setHours(23, 59, 59); // End of day
      filtered = filtered.filter(review => new Date(review.submitted_at) <= toDate);
    }
    
    setFilteredReviews(filtered);
    setPage(0); // Reset pagination
  }, [reviews, filters]);

  // ===== EFFECTS =====
  useEffect(() => {
    loadCVReviews();
  }, [loadCVReviews]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  // ===== HANDLERS =====
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const clearFilters = () => {
    setFilters({
      status: '',
      panelist: '',
      rating_min: 0,
      rating_max: 5,
      search: '',
      date_from: '',
      date_to: ''
    });
  };

  const handleViewDetails = (review) => {
    setSelectedReview(review);
    setDetailDialogOpen(true);
  };

  // ===== UTILITY FUNCTIONS =====
  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'success';
      case 'rejected': return 'error';
      case 'needs_review': return 'warning';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved': return <CheckCircle />;
      case 'rejected': return <Cancel />;
      case 'needs_review': return <HelpOutline />;
      default: return <Schedule />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return isValid(date) ? format(date, 'MMM dd, yyyy HH:mm') : 'Invalid Date';
  };

  // Paginated data
  const paginatedReviews = filteredReviews.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
          📝 CV Reviews Management
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Monitor and analyze panelist feedback on candidate CVs
        </Typography>
      </Box>

      {/*
        Analytics cards.
        Rendered with the shared StatCard so they match the manager, vendor and
        panelist dashboards. Previously four cards each carried a different
        two-stop gradient with white text -- and the colours did not track
        meaning: "Rejected CVs" was blue while "Approved CVs" was pink, so the
        palette actively worked against reading the row. Tones now follow the
        status vocabulary in theme.js: approved is success, rejected is danger.
      */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="Total reviews"
            value={analytics.total_reviews.toLocaleString()}
            icon={<RateReview />}
            tone="info"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="Approved CVs"
            value={analytics.approved}
            caption={analytics.total_reviews > 0
              ? `${((analytics.approved / analytics.total_reviews) * 100).toFixed(1)}% of reviews`
              : 'No reviews yet'}
            icon={<CheckCircle />}
            tone="success"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="Rejected CVs"
            value={analytics.rejected}
            caption={analytics.total_reviews > 0
              ? `${((analytics.rejected / analytics.total_reviews) * 100).toFixed(1)}% of reviews`
              : 'No reviews yet'}
            icon={<Cancel />}
            tone="danger"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="Average rating"
            value={`${analytics.avg_rating}/5`}
            caption={
              <Rating value={Number(analytics.avg_rating) || 0} readOnly size="small"
                      precision={0.1} />
            }
            icon={<Star />}
            tone="warning"
          />
        </Grid>
      </Grid>

      {/* Filters */}
      <Accordion expanded={filtersExpanded} onChange={() => setFiltersExpanded(!filtersExpanded)}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="h6">
            <FilterAlt sx={{ mr: 1, verticalAlign: 'middle' }} />
            Advanced Filters
            {Object.values(filters).some(v => v !== '' && v !== 0 && v !== 5) && (
              <Badge badgeContent="Active" color="primary" sx={{ ml: 2 }} />
            )}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Search"
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                placeholder="Search candidates, notes, feedback..."
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  label="Status"
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                >
                  <MenuItem value="">All Status</MenuItem>
                  <MenuItem value="approved">✅ Approved</MenuItem>
                  <MenuItem value="rejected">❌ Rejected</MenuItem>
                  <MenuItem value="needs_review">🔄 Needs Review</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Panelist"
                value={filters.panelist}
                onChange={(e) => handleFilterChange('panelist', e.target.value)}
                placeholder="Filter by panelist name..."
              />
            </Grid>
            
            <Grid item xs={12} md={3}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Button
                  variant="outlined"
                  onClick={clearFilters}
                  disabled={!Object.values(filters).some(v => v !== '' && v !== 0 && v !== 5)}
                >
                  Clear Filters
                </Button>
                <Button
                  variant="contained"
                  startIcon={<Refresh />}
                  onClick={loadCVReviews}
                  disabled={loading}
                >
                  Refresh
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Results */}
      <Paper sx={{ mt: 3 }}>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6">
            CV Reviews ({filteredReviews.length.toLocaleString()} results)
          </Typography>
        </Box>

        {loading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <CircularProgress />
            <Typography sx={{ mt: 2 }}>Loading CV reviews...</Typography>
          </Box>
        ) : filteredReviews.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <RateReview sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.disabled">
              No CV reviews found
            </Typography>
            <Typography color="text.disabled">
              {Object.values(filters).some(v => v !== '' && v !== 0 && v !== 5)
                ? 'Try adjusting your filters'
                : 'No reviews have been submitted yet'
              }
            </Typography>
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Candidate</TableCell>
                    <TableCell>Panelist</TableCell>
                    <TableCell>Rating</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Scores</TableCell>
                    <TableCell>Submitted</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedReviews.map((review) => (
                    <TableRow key={review.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                            {review.candidate_name?.charAt(0) || 'C'}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              {review.candidate_name || `Candidate #${review.candidate_id}`}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              ID: {review.candidate_id}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {review.panelist_name || `Panelist #${review.panelist_id}`}
                        </Typography>
                      </TableCell>
                      
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Rating value={review.rating} readOnly size="small" />
                          <Typography variant="body2" sx={{ ml: 1, fontWeight: 600 }}>
                            {review.rating}/5
                          </Typography>
                        </Box>
                      </TableCell>
                      
                      <TableCell>
                        <Chip
                          icon={getStatusIcon(review.status)}
                          label={review.status?.replace('_', ' ').toUpperCase() || 'PENDING'}
                          color={getStatusColor(review.status)}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      
                      <TableCell>
                        <Stack spacing={0.5}>
                          {review.technical_skills > 0 && (
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Typography variant="caption" sx={{ minWidth: 80 }}>Tech:</Typography>
                              <LinearProgress
                                variant="determinate"
                                value={(review.technical_skills / 5) * 100}
                                sx={{ width: 60, mr: 1 }}
                              />
                              <Typography variant="caption">{review.technical_skills}/5</Typography>
                            </Box>
                          )}
                          {review.experience_relevance > 0 && (
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Typography variant="caption" sx={{ minWidth: 80 }}>Exp:</Typography>
                              <LinearProgress
                                variant="determinate"
                                value={(review.experience_relevance / 5) * 100}
                                sx={{ width: 60, mr: 1 }}
                                color="secondary"
                              />
                              <Typography variant="caption">{review.experience_relevance}/5</Typography>
                            </Box>
                          )}
                        </Stack>
                      </TableCell>
                      
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(review.submitted_at)}
                        </Typography>
                      </TableCell>
                      
                      <TableCell>
                        {/* Clipboard, not an eye: this opens the review record,
                            while the resume icons beside it act on the file. */}
                        <Tooltip title="View review details">
                          <IconButton
                            size="small"
                            onClick={() => handleViewDetails(review)}
                          >
                            <Assignment fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              component="div"
              count={filteredReviews.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(event, newPage) => setPage(newPage)}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(parseInt(event.target.value, 10));
                setPage(0);
              }}
            />
          </>
        )}
      </Paper>

      {/* Detail Dialog */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">
              CV Review Details
            </Typography>
            <IconButton onClick={() => setDetailDialogOpen(false)}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        
        {selectedReview && (
          <DialogContent dividers>
            <Grid container spacing={3}>
              {/* Review Overview */}
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2 }}>Review Overview</Typography>
                    
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Candidate:</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {selectedReview.candidate_name || `Candidate #${selectedReview.candidate_id}`}
                        </Typography>
                      </Grid>
                      
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Panelist:</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {selectedReview.panelist_name || `Panelist #${selectedReview.panelist_id}`}
                        </Typography>
                      </Grid>
                      
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Overall Rating:</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Rating value={selectedReview.rating} readOnly />
                          <Typography sx={{ ml: 1, fontWeight: 600 }}>
                            {selectedReview.rating}/5
                          </Typography>
                        </Box>
                      </Grid>
                      
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Status:</Typography>
                        <Chip
                          icon={getStatusIcon(selectedReview.status)}
                          label={selectedReview.status?.replace('_', ' ').toUpperCase() || 'PENDING'}
                          color={getStatusColor(selectedReview.status)}
                          variant="outlined"
                        />
                      </Grid>
                      
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">Submitted:</Typography>
                        <Typography variant="body1">
                          {formatDate(selectedReview.submitted_at)}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Detailed Scores */}
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2 }}>Detailed Assessment</Typography>
                    
                    <Grid container spacing={2}>
                      {[
                        { key: 'technical_skills', label: 'Technical Skills' },
                        { key: 'experience_relevance', label: 'Experience Relevance' },
                        { key: 'communication_skills', label: 'Communication Skills' },
                        { key: 'overall_impression', label: 'Overall Impression' },
                        { key: 'education_fit', label: 'Education Fit' },
                        { key: 'domain_knowledge', label: 'Domain Knowledge' }
                      ].map((item) => (
                        selectedReview[item.key] > 0 && (
                          <Grid item xs={12} sm={6} key={item.key}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                              <Typography variant="body2" sx={{ minWidth: 150 }}>
                                {item.label}:
                              </Typography>
                              <LinearProgress
                                variant="determinate"
                                value={(selectedReview[item.key] / 5) * 100}
                                sx={{ flexGrow: 1, mx: 2 }}
                              />
                              <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 30 }}>
                                {selectedReview[item.key]}/5
                              </Typography>
                            </Box>
                          </Grid>
                        )
                      ))}
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Qualitative Feedback */}
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2 }}>Qualitative Feedback</Typography>

                    {selectedReview.strengths && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="success.main" sx={{ fontWeight: 600, mb: 1 }}>
                          💪 Strengths:
                        </Typography>
                        <Typography variant="body2" sx={{ pl: 2, borderLeft: 3, borderColor: 'success.light' }}>
                          {selectedReview.strengths}
                        </Typography>
                      </Box>
                    )}

                    {selectedReview.weaknesses && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="warning.main" sx={{ fontWeight: 600, mb: 1 }}>
                          ⚠️ Areas for Improvement:
                        </Typography>
                        <Typography variant="body2" sx={{ pl: 2, borderLeft: 3, borderColor: 'warning.light' }}>
                          {selectedReview.weaknesses}
                        </Typography>
                      </Box>
                    )}

                    {selectedReview.recommendations && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="info.main" sx={{ fontWeight: 600, mb: 1 }}>
                          💡 Recommendations:
                        </Typography>
                        <Typography variant="body2" sx={{ pl: 2, borderLeft: 3, borderColor: 'info.light' }}>
                          {selectedReview.recommendations}
                        </Typography>
                      </Box>
                    )}

                    {selectedReview.notes && (
                      <Box>
                        <Typography variant="subtitle2" color="text.primary" sx={{ fontWeight: 600, mb: 1 }}>
                          📝 Additional Notes:
                        </Typography>
                        <Typography variant="body2" sx={{ pl: 2, borderLeft: 3, borderColor: 'grey.300' }}>
                          {selectedReview.notes}
                        </Typography>
                      </Box>
                    )}

                    {!selectedReview.strengths && !selectedReview.weaknesses &&
                     !selectedReview.recommendations && !selectedReview.notes && (
                      <Typography color="text.secondary" style={{ fontStyle: 'italic' }}>
                        No additional feedback provided
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* Associated Hashtags */}
              {selectedReview.hashtags_list && selectedReview.hashtags_list.length > 0 && (
                <Grid item xs={12}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                        <LocalOffer sx={{ mr: 1, fontSize: '1.2rem' }} />
                        Associated Hashtags
                      </Typography>

                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {selectedReview.hashtags_list.map((hashtag, index) => (
                          <Chip
                            key={index}
                            label={`#${hashtag}`}
                            size="small"
                            variant="outlined"
                            color="primary"
                            sx={{
                              fontSize: '0.75rem',
                              '&:hover': {
                                backgroundColor: 'primary.light',
                                color: 'white'
                              }
                            }}
                          />
                        ))}
                      </Box>

                      {selectedReview.hashtags_list.length === 0 && (
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          No hashtags generated for this feedback
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>
          </DialogContent>
        )}
        
        <DialogActions sx={{ justifyContent: 'space-between' }}>
          {/* The resume behind the review, so an admin can check the source. */}
          {selectedReview?.candidate_id ? (
            <ResumeActions
              candidateId={selectedReview.candidate_id}
              candidateName={selectedReview.candidate_name}
            />
          ) : <span />}
          <Button onClick={() => setDetailDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CandidateReviews;
